import dayjs from 'dayjs';

import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';

import { DEFAULT_TIME_FILTER } from './TimeFilter.constants';
import type {
  ExtendedData,
  TimePresetField,
  TimePresetKey,
  TimePresetKeyWithField
} from './TimeFilter.types';

/**
 * Get the filter string, field name and prefix from a time filter string
 * @param datetime string
 * @returns { timeFilter: string, timeFilterField: string, prefix: string }
 * @example
 * getTimeFilterValues('last-07:00:00:00|C')
 * // returns { timeFilterKey: 'last-07:00:00:00', timeFilterField: 'C', prefix: 'last' }
 */
export const getTimeFilterValues = (
  datetime = DEFAULT_TIME_FILTER
): {
  timeFilterKey: TimePresetKey;
  timeFilterField: TimePresetField;
  prefix: string;
} => {
  const [timeFilterKey = 'last-07:00:00:00', timeFilterField = 'C'] =
    datetime.split('|') as [TimePresetKey, TimePresetField];
  const prefix: string = timeFilterKey.split('-')[0] || 'last';

  return {
    timeFilterKey,
    timeFilterField,
    prefix
  };
};

export const getCustomTimeFilterValues = (
  datetime: string // e.g. 'custom-2023-01-01,2023-01-31|C'
): {
  from: string;
  to: string;
  source: TimePresetField;
} => {
  const source = datetime.includes('|U') ? 'U' : 'C';
  const [from, to] =
    datetime.replace('custom-', '').split('|').shift()?.split(',') || [];

  return {
    from,
    to,
    source
  };
};

// Calculate the time range based on the selected time filter and return from and to dates in 'YYYY-MM-DD HH:mm:00' format
// eg. 'last-07:00:00:00|C' will return { from: '2025-01-01 00:00:00', to: '2025-01-08 02:25:00' }
export const getTimeRangeFromFilter = (
  selectedTime: TimePresetKeyWithField
) => {
  const { timeFilterKey, prefix } = getTimeFilterValues(selectedTime);
  let from = null;
  let to = null;

  switch (prefix) {
    case 'today':
      from = dayjs().startOf('day');
      break;

    case 'yesterday':
      from = dayjs().subtract(1, 'day').startOf('day');
      to = dayjs().startOf('day');
      break;

    case 'week':
      dayjs.Ls.en.weekStart = 1; // Set week start to Monday
      from = dayjs().startOf('week');
      break;

    case 'month':
      from = dayjs().startOf('month');
      break;

    case 'all':
      from = dayjs()
        .subtract(10 * 365, 'day')
        .startOf('day'); // 10 years back
      break;

    case 'last': {
      const [days, hours, minutes] = timeFilterKey
        .replace('last-', '')
        .split(':')
        .map(Number);

      from = (days > 0 ? dayjs().startOf('day') : dayjs()) // Start from the beginning of the day if days > 0
        .subtract(days, 'day')
        .subtract(hours, 'hour')
        .subtract(minutes, 'minute');
      break;
    }
    case 'custom':
      ({ from, to } = getCustomTimeFilterValues(timeFilterKey));
      if (!from) {
        from = dayjs()
          .subtract(365 * 10, 'day')
          .startOf('day');
      }
      if (!to) {
        to = dayjs().endOf('day');
      }
      break;

    default:
      break;
  }

  // Using start of hour to avoid seconds and minutes causing caching in react query that uses from and to as a key
  return {
    from: from
      ? dayjs(from).startOf('hour').format(YEAR_MONTH_DAY_TIME_FORMAT)
      : null,
    to: to ? dayjs(to).startOf('hour').format(YEAR_MONTH_DAY_TIME_FORMAT) : null
  };
};

export const filterByDatetime = <T extends ExtendedData>(
  data: T[],
  selectedTime: TimePresetKeyWithField,
  timeFieldKey?: string
): T[] => {
  const field = (timeFieldKey ??
    (getTimeFilterValues(selectedTime).timeFilterField === 'C'
      ? 'published_at'
      : 'updated_at')) as keyof ExtendedData;

  let { from, to } = getTimeRangeFromFilter(selectedTime);
  to = to || dayjs().endOf('day').toISOString();
  from = from || dayjs(0).toISOString();

  return data.filter((item) => {
    const itemDate = dayjs.utc(item[field] as string);
    return itemDate.isAfter(from) && itemDate.isBefore(to);
  });
};

export const parseTimeFilter = (
  timeFilter: TimePresetKeyWithField
): {
  from: string | null;
  to: string | null;
  column: string | null;
} => {
  if (!timeFilter) return { from: null, to: null, column: null };

  const { from, to } = getTimeRangeFromFilter(timeFilter);
  const column = timeFilter.split('|').pop() || null;
  return {
    from,
    to,
    column
  };
};
